import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import ItemService from "../../../../services/commonService";
import Constants from "../../../../common/Constants";
import Loader from "../../../../common/Loader";
import { toastFunc } from "../../../common/util";
import "./SummaryReport.scss";

const STATUS_OPTIONS = [
  { value: "InProgress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

const REPORT_MODES = ["Cumulative", "Detailed"];

const STATUS_COLUMNS = [
  { key: "consumption", label: "Consumption", className: "consumption" },
  { key: "ok", label: "OK", className: "ok" },
  { key: "reject", label: "Reject", className: "reject" },
  { key: "rework", label: "Rework", className: "rework" },
  { key: "completedDate", label: "Completed Date", className: "date" },
];

const EMPTY_FILTERS = { sizeCode: "", customer: "", jobcard: "" };

const Quantity = ({ value, type }) => (
  <span className={`summary-report__quantity summary-report__quantity--${type} ${
    Number(value) === 0 ? "summary-report__quantity--zero" : ""
  }`}>
    {Number(value || 0).toLocaleString()}
  </span>
);

const ReasonBreakdown = ({ value, type, reasons = [] }) => (
  <div className="summary-report__reason-breakdown">
    <Quantity value={value} type={type} />
    {reasons.length > 0 && (
      <ul>
        {reasons.map((reason) => (
          <li key={`${reason.reasonCode}-${reason.reasonName}`}>
            <span title={reason.reasonName}>{reason.reasonName}</span>
            <strong>{Number(reason.count || 0).toLocaleString()}</strong>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ConsumptionBreakdown = ({ value, details = [] }) => (
  <div className="summary-report__reason-breakdown summary-report__consumption-breakdown">
    <Quantity value={value} type="consumption" />
    {details.length > 0 && (
      <ul>
        {details.map((detail) => (
          <li key={`${detail.consumptionType}-${detail.consumableSize}`}>
            <span title={`${detail.consumptionType} - ${detail.consumableSize}`}>
              {detail.consumptionType}
              <small>Size: {detail.consumableSize}</small>
            </span>
            <strong>{Number(detail.count || 0).toLocaleString()}</strong>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const SummaryReport = () => {
  const [status, setStatus] = useState("InProgress");
  const [reportMode, setReportMode] = useState("Cumulative");
  const [month, setMonth] = useState(moment().format("YYYY-MM"));
  const [filterInputs, setFilterInputs] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const requestId = useRef(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadReport = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    try {
      const response = await ItemService.getTransactionListDataById({
        url: Constants.REPORTS.SUMMARYREPORT.list,
        status,
        reportMode,
        month,
        filters,
        page,
        perPage,
      });

      if (currentRequest !== requestId.current) return;
      if (!response || (response.message && !response.items)) {
        throw new Error(response?.message || "Unable to load summary report.");
      }
      setItems(response?.items || []);
      setDepartments(response?.departments || []);
      setTotalCount(response?.totalCount || 0);
      setTotalPages(response?.totalPages || 0);
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      setItems([]);
      setTotalCount(0);
      setTotalPages(0);
      toastFunc(
        Constants.MASTER.TOAST.error,
        error.message || "Unable to load summary report.",
        4000,
      );
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [filters, month, page, perPage, reportMode, status]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const changeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setFilters({
      sizeCode: filterInputs.sizeCode.trim(),
      customer: filterInputs.customer.trim(),
      jobcard: filterInputs.jobcard.trim(),
    });
    setPage(1);
  };

  const clearFilters = () => {
    setFilterInputs(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const updateFilter = (field, value) => {
    setFilterInputs((current) => ({ ...current, [field]: value }));
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      await ItemService.downloadReport({
        url: Constants.REPORTS.SUMMARYREPORT.list,
        status,
        reportMode,
        month,
        filters,
      });
      toastFunc(Constants.MASTER.TOAST.success, "Summary report downloaded.", 2500);
    } catch (error) {
      toastFunc(
        Constants.MASTER.TOAST.error,
        error.message || "Unable to download summary report.",
        4000,
      );
    } finally {
      setDownloading(false);
    }
  };

  const visibleFrom = totalCount ? (page - 1) * perPage + 1 : 0;
  const visibleTo = Math.min(page * perPage, totalCount);

  return (
    <main
      className={`summary-report main ${
        reportMode === "Detailed" ? "summary-report--detailed" : ""
      }`}
      id="main"
    >
      <section className="summary-report__hero">
        <div>
          <div className="summary-report__eyebrow">Production Reports</div>
          <h1>Department Summary Report</h1>
          <p>
            Jobcard quantities and completion dates across every production department.
          </p>
        </div>
        <div className="summary-report__hero-metrics">
          <div>
            <span>Jobcards</span>
            <strong>{totalCount.toLocaleString()}</strong>
          </div>
          <div>
            <span>Departments</span>
            <strong>{departments.length}</strong>
          </div>
        </div>
      </section>

      <section className="summary-report__toolbar" aria-label="Summary report filters">
        <div className="summary-report__status-switch" role="group" aria-label="Report status">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={status === option.value ? "is-active" : ""}
              onClick={() => changeStatus(option.value)}
            >
              <span className={`summary-report__status-dot summary-report__status-dot--${option.value}`} />
              {option.label}
            </button>
          ))}
        </div>

        <label className="summary-report__field">
          <span>{status === "Completed" ? "Completion Month" : "Report Month"}</span>
          <input
            type="month"
            value={month}
            max={moment().format("YYYY-MM")}
            required
            onChange={(event) => {
              if (event.target.value) {
                setMonth(event.target.value);
                setPage(1);
              }
            }}
          />
        </label>

        <form className="summary-report__filters" onSubmit={applyFilters}>
          <label>
            <span>Size Code</span>
            <input
              value={filterInputs.sizeCode}
              onChange={(event) => updateFilter("sizeCode", event.target.value)}
              placeholder="Enter size code"
            />
          </label>
          <label>
            <span>Customer</span>
            <input
              value={filterInputs.customer}
              onChange={(event) => updateFilter("customer", event.target.value)}
              placeholder="Code or customer name"
            />
          </label>
          <label>
            <span>Jobcard</span>
            <input
              value={filterInputs.jobcard}
              onChange={(event) => updateFilter("jobcard", event.target.value)}
              placeholder="Enter jobcard"
            />
          </label>
          <button className="summary-report__filter-apply" type="submit">
            <i className="bi bi-search" /> Apply
          </button>
          <button className="summary-report__filter-clear" type="button" onClick={clearFilters}>
            Clear
          </button>
        </form>

        <button
          type="button"
          className="summary-report__download"
          onClick={downloadExcel}
          disabled={downloading || loading}
        >
          <i className={`bi ${downloading ? "bi-hourglass-split" : "bi-file-earmark-excel"}`} />
          {downloading ? "Preparing..." : "Download Excel"}
        </button>
      </section>

      <section className="summary-report__content">
        <div className="summary-report__table-heading">
          <div>
            <h2>{status === "Completed" ? "Completed Jobcards" : "In-progress Jobcards"}</h2>
            <p>
              {status === "Completed"
                ? `Batch cards that finished DEPT018 during ${moment(month, "YYYY-MM").format("MMMM YYYY")}`
                : `Unfinished jobcards with confirmation records through ${month === moment().format("YYYY-MM") ? "today" : moment(month, "YYYY-MM").endOf("month").format("DD MMM YYYY")}. Department quantities and reasons are cumulative through this cutoff.`}
            </p>
          </div>
          <div className="summary-report__table-actions">
            <div
              className="summary-report__mode-switch"
              role="group"
              aria-label="Report detail level"
            >
              {REPORT_MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={reportMode === mode ? "is-active" : ""}
                  onClick={() => {
                    setReportMode(mode);
                    setPage(1);
                  }}
                >
                  <i
                    className={`bi ${
                      mode === "Detailed" ? "bi-list-ul" : "bi-grid"
                    }`}
                  />
                  {mode}
                </button>
              ))}
            </div>
            <span className="summary-report__scroll-hint">
              <i className="bi bi-arrow-left-right" /> Scroll horizontally for departments
            </span>
          </div>
        </div>

        <div className="summary-report__table-shell">
          <Loader isLoading={loading} className="text-center" />
          {!loading && (
            <table className="summary-report__table">
              <thead>
                <tr>
                  <th rowSpan="2" className="summary-report__sticky summary-report__sticky--size">
                    Size
                  </th>
                  <th rowSpan="2" className="summary-report__sticky summary-report__sticky--customer">
                    Customer
                  </th>
                  <th rowSpan="2" className="summary-report__sticky summary-report__sticky--jobcard">
                    Jobcard
                  </th>
                  {departments.map((department, index) => (
                    <th
                      key={department.deptCode}
                      colSpan={STATUS_COLUMNS.length}
                      className={`summary-report__department-group ${index % 2 ? "is-alternate" : ""}`}
                    >
                      <span>{department.deptCode}</span>
                      <strong>{department.deptName}</strong>
                    </th>
                  ))}
                </tr>
                <tr>
                  {departments.flatMap((department, index) =>
                    STATUS_COLUMNS.map((column) => (
                      <th
                        key={`${department.deptCode}-${column.key}`}
                        className={`summary-report__subhead summary-report__subhead--${column.className} ${
                          index % 2 ? "is-alternate" : ""
                        }`}
                      >
                        {column.label}
                      </th>
                    )),
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.jobcard}>
                    <td className="summary-report__sticky summary-report__sticky--size">
                      <strong>{row.sizeCode || "-"}</strong>
                      <span>{row.sizeName}</span>
                    </td>
                    <td className="summary-report__sticky summary-report__sticky--customer">
                      <strong>{row.customerName || "-"}</strong>
                      <span>{row.customerCode}</span>
                    </td>
                    <td className="summary-report__sticky summary-report__sticky--jobcard">
                      <span className="summary-report__jobcard">{row.jobcard}</span>
                    </td>
                    {departments.flatMap((department) => {
                      const detail = row.departments?.[department.deptCode] || {};
                      return STATUS_COLUMNS.map((column) => (
                        <td
                          key={`${row.jobcard}-${department.deptCode}-${column.key}`}
                          className={`summary-report__value summary-report__value--${column.className}`}
                        >
                          {column.key === "completedDate" ? (
                            detail.completedDate ? (
                              <span className="summary-report__date">{detail.completedDate}</span>
                            ) : (
                              <span className="summary-report__empty">&mdash;</span>
                            )
                          ) : reportMode === "Detailed" &&
                            column.key === "consumption" ? (
                            <ConsumptionBreakdown
                              value={detail.consumption}
                              details={detail.consumptionDetails}
                            />
                          ) : reportMode === "Detailed" &&
                            ["reject", "rework"].includes(column.key) ? (
                            <ReasonBreakdown
                              value={detail[column.key]}
                              type={column.className}
                              reasons={detail[`${column.key}Reasons`]}
                            />
                          ) : (
                            <Quantity value={detail[column.key]} type={column.className} />
                          )}
                        </td>
                      ));
                    })}
                  </tr>
                ))}
                {!items.length && (
                  <tr>
                    <td colSpan={3 + departments.length * STATUS_COLUMNS.length}>
                      <div className="summary-report__empty-state">
                        <i className="bi bi-inbox" />
                        <strong>No matching jobcards</strong>
                        <span>Try another status, month, or filter value.</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <footer className="summary-report__pagination">
          <div>
            Showing <strong>{visibleFrom}-{visibleTo}</strong> of <strong>{totalCount}</strong>
          </div>
          <label>
            Rows per page
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>
          <div className="summary-report__page-controls">
            <button type="button" onClick={() => setPage(1)} disabled={page <= 1} aria-label="First page">
              <i className="bi bi-chevron-bar-left" />
            </button>
            <button type="button" onClick={() => setPage((value) => value - 1)} disabled={page <= 1} aria-label="Previous page">
              <i className="bi bi-chevron-left" />
            </button>
            <span>Page <strong>{page}</strong> of <strong>{Math.max(totalPages, 1)}</strong></span>
            <button type="button" onClick={() => setPage((value) => value + 1)} disabled={page >= totalPages} aria-label="Next page">
              <i className="bi bi-chevron-right" />
            </button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages} aria-label="Last page">
              <i className="bi bi-chevron-bar-right" />
            </button>
          </div>
        </footer>
      </section>
    </main>
  );
};

export default SummaryReport;
